/**
 * useAssistant — React hook for the floating AI assistant.
 *
 * Manages: chat messages, MCP tool calls, page context, confirmation flow.
 * Uses the Context Engine (semantic search) for intent resolution.
 *
 * Pipeline:
 * 1. Confirmation flow ("yes" after preview)
 * 2. Slash commands (/toolname {json})
 * 3. Navigation intents (client-side routing)
 * 4. Greetings → briefing
 * 5. Everything else → assistant_search
 */

import { useState, useCallback, useRef, useEffect } from 'react'
import { useLocation, useNavigate } from 'react-router'
import { useTranslation } from 'react-i18next'
import { mcpClient, type MCPToolResult, type MCPResultMeta } from './mcp-client'
import { governanceLabelsService } from '@/services/governance-labels.service'
import { auditService } from '@/services/audit.service'
import { agentHostService } from '@/services/agentHostService'
import { intelligenceService } from '@/services/intelligence.service'
import type { GovernanceLabel, LabelCategory } from '@/types/governance-labels'
import { trackSearchQuery, trackSearchClick } from '@/lib/telemetry'
import {
  useAssistantStore,
  type AssistantMessage as StoreAssistantMessage,
  type PendingConfirmation as StorePendingConfirmation,
} from '@/shared/stores/assistant.store'

// Re-export so existing consumers (AgionAssistant.tsx) keep working.
// Messages now live in zustand — the store is the source of truth —
// but the legacy local types are preserved so call sites don't churn.
export type AssistantMessage = StoreAssistantMessage
type PendingConfirmation = StorePendingConfirmation

// ============================================================
// Conversation Compaction
// ============================================================

/** Compact conversation after this many total messages */
const COMPACT_THRESHOLD = 8
/** Always keep the last N messages in full (not compacted) */
const KEEP_RECENT_TURNS = 4

/**
 * Compacts old messages into a single system summary message.
 * Preserves recent messages in full for continuity.
 *
 * Uses the `summary` field from tool response _meta when available,
 * otherwise truncates the content.
 */
function compactMessages(messages: AssistantMessage[]): AssistantMessage[] {
  if (messages.length <= COMPACT_THRESHOLD) return messages

  const cutoff = messages.length - KEEP_RECENT_TURNS
  const oldMessages = messages.slice(0, cutoff)
  const recentMessages = messages.slice(cutoff)

  // Build compacted summary from old messages
  const summaryLines: string[] = []
  for (const msg of oldMessages) {
    if (msg.role === 'assistant' && msg.meta?.summary) {
      summaryLines.push(`- ${msg.meta.summary}`)
    } else if (msg.role === 'user') {
      // Keep user messages as brief context
      const truncated = msg.content.length > 60
        ? msg.content.slice(0, 57) + '...'
        : msg.content
      summaryLines.push(`- User: ${truncated}`)
    } else if (msg.role === 'assistant' && !msg.meta?.summary) {
      // No summary available — truncate content
      const truncated = msg.content.length > 80
        ? msg.content.slice(0, 77) + '...'
        : msg.content
      summaryLines.push(`- ${truncated}`)
    }
    // Skip system messages (already context)
  }

  if (summaryLines.length === 0) return messages

  const compactedMessage: AssistantMessage = {
    id: 'compacted-context',
    role: 'system',
    content: `Session context (${oldMessages.length} earlier messages compacted):\n${summaryLines.join('\n')}`,
    timestamp: oldMessages[0]?.timestamp ?? Date.now(),
  }

  return [compactedMessage, ...recentMessages]
}

interface LabelCatalog {
  labels: GovernanceLabel[]
  categories: LabelCategory[]
  loadedAt: number
}

// Messages + pending confirmation now live in the zustand store (see
// assistant.store.ts). Keeping only the session-ephemeral fields
// (isLoading / error / isModelConnected / labelCatalog) here — those
// are fine to discard when the panel unmounts.
interface AssistantState {
  isLoading: boolean
  isModelConnected: boolean | null
  error: string | null
  labelCatalog: LabelCatalog | null
}

export function useAssistant() {
  const { t } = useTranslation('components')
  // Ephemeral / per-panel state stays in useState.
  const [state, setState] = useState<AssistantState>({
    isLoading: false,
    isModelConnected: null,
    error: null,
    labelCatalog: null,
  })
  // Persisted chat state in the zustand store so messages survive
  // panel close/reopen + SPA navigation (sessionStorage-backed).
  const messages = useAssistantStore((s) => s.messages)
  const pendingConfirmation = useAssistantStore((s) => s.pendingConfirmation)
  const storeAddMessage = useAssistantStore((s) => s.addMessage)
  const storeSetMessages = useAssistantStore((s) => s.setMessages)
  const storeSetPending = useAssistantStore((s) => s.setPendingConfirmation)

  const location = useLocation()
  const navigate = useNavigate()
  const lastRouteRef = useRef('')

  useEffect(() => {
    checkModelConnected()
  }, [])

  // Load the org's governance labels once at chat-panel mount so the
  // rule-creation parser can match user phrases ("PII", "personal
  // data", "credit card") against the org's actual label keys —
  // never hardcoded strings. Refreshed if the chat is open longer
  // than 5 minutes (rule labels rarely change).
  useEffect(() => {
    let cancelled = false
    const loadLabels = async () => {
      try {
        const [labelResp, categories] = await Promise.all([
          governanceLabelsService.getLabels({ isActive: true }),
          governanceLabelsService.getCategories(),
        ])
        if (cancelled) return
        const labels = (labelResp as any)?.labels ?? []
        setState(s => ({
          ...s,
          labelCatalog: { labels, categories, loadedAt: Date.now() },
        }))
      } catch {
        // Silent fall-through. createRuleFromText degrades to its
        // hardcoded fallback values when labelCatalog is null.
      }
    }
    loadLabels()
    return () => { cancelled = true }
  }, [])

  useEffect(() => {
    const pageContextRoute = `${location.pathname}${location.search}`
    if (pageContextRoute !== lastRouteRef.current) {
      lastRouteRef.current = pageContextRoute
      mcpClient.callTool('page_context', {
        route: pageContextRoute,
        page_title: document.title,
      }).catch(() => {})
    }
  }, [location.pathname, location.search])

  const checkModelConnected = useCallback(async () => {
    try {
      const result = await mcpClient.callTool('list_providers')
      const text = result.content[0]?.text ?? ''
      setState(s => ({ ...s, isModelConnected: !text.includes('No AI providers') }))
    } catch {
      setState(s => ({ ...s, isModelConnected: true }))
    }
  }, [])

  const addMessage = useCallback((msg: Omit<AssistantMessage, 'id' | 'timestamp'>) => {
    const message = storeAddMessage(msg)
    // Run compaction on the new rolling window — store holds the
    // authoritative list, so we re-set it when the compactor collapses
    // old turns into a single system summary.
    const compacted = compactMessages(useAssistantStore.getState().messages)
    if (compacted.length !== useAssistantStore.getState().messages.length) {
      storeSetMessages(compacted)
    }
    return message.id
  }, [storeAddMessage, storeSetMessages])

  // Track the last query for click tracking
  const lastQueryRef = useRef<string>('')

  const processMeta = useCallback((meta: MCPResultMeta | undefined, query?: string) => {
    if (!meta) return
    if (meta.navigateTo) {
      // Track navigation as a search result click
      if (query || lastQueryRef.current) {
        const searchQuery = query || lastQueryRef.current
        // Extract result type from route (e.g., /missions/123 -> mission)
        const routeParts = meta.navigateTo.split('/').filter(Boolean)
        const resultType = routeParts[0] || 'unknown'
        const resultId = routeParts[1] || meta.navigateTo
        trackSearchClick(searchQuery, resultId, 1, resultType)
      }
      navigate(meta.navigateTo)
    }
  }, [navigate])

  const sendMessage = useCallback(async (text: string) => {
    if (!text.trim() || state.isLoading) return

    // Quality gate: queries with no semantic content (`?`, `xx`, `aaaa`)
    // never round-trip the MCP. Render the user turn so the chat shows
    // they were heard, plus an inline tip that nudges them toward a
    // real question. The matching server-side floor is
    // `query_too_short_for_tier2` in retrieve_dispatcher.rs.
    if (isQueryTooDegenerate(text)) {
      addMessage({ role: 'user', content: text })
      addMessage({
        role: 'assistant',
        content: t('assistant.tooShort'),
      })
      return
    }

    addMessage({ role: 'user', content: text })
    setState(s => ({ ...s, isLoading: true, error: null }))

    // Stamp the user's typed text onto every MCP call this routing
    // round produces (X-Intent-Topic header), so the admin-mcp dispatcher
    // can thread it into the precedent narrative. Cleared in the
    // finally block so subsequent speculative fetches don't inherit it.
    mcpClient.setUserIntent(text)

    const startTime = Date.now()
    try {
      const result = await executeIntent(
        text,
        pendingConfirmation,
        `${location.pathname}${location.search}`,
        navigate,
        state.labelCatalog,
      )
      const latencyMs = Date.now() - startTime

      // Track search telemetry
      const resultCount = result.content?.length ?? 0
      const classification = result._meta?.classification
      trackSearchQuery(text, resultCount, classification, latencyMs)

      // Store query for click tracking
      lastQueryRef.current = text

      const lower = text.toLowerCase().trim()
      const isConfirm = /^(yes|confirm|do it|go ahead|proceed|ok|sure)$/i.test(lower)

      let newPending: PendingConfirmation | null = null
      if (result.content[0]?.text?.includes('Preview:') && !isConfirm) {
        newPending = (result as any)._pending || null
      }

      addMessage({
        role: 'assistant',
        content: result.content[0]?.text ?? 'Done.',
        meta: result._meta,
        isError: result.isError,
      })

      processMeta(result._meta, text)
      storeSetPending(isConfirm ? null : newPending)
      setState(s => ({ ...s, isLoading: false }))
    } catch (err) {
      addMessage({
        role: 'assistant',
        content: `Sorry, something went wrong: ${err instanceof Error ? err.message : 'Unknown error'}`,
        isError: true,
      })
      setState(s => ({ ...s, isLoading: false }))
    } finally {
      // Ensure the intent stamp doesn't leak into speculative tool
      // calls that fire outside a user-message context (page-context
      // fetches, init handshakes, etc.).
      mcpClient.clearUserIntent()
    }
  }, [pendingConfirmation, state.labelCatalog, addMessage, processMeta, location.pathname, location.search, navigate, storeSetPending])

  const stop = useCallback(() => {
    setState(s => ({ ...s, isLoading: false }))
  }, [])

  /**
   * executeAction directly invokes an MCP tool with the given
   * arguments, bypassing the natural-language routing pipeline.
   * Used by confirm-button clicks: the chat-ui renders a button
   * from _meta.confirmAction and on click calls this with the
   * stored {tool, arguments} payload. Response is appended as an
   * assistant message just like sendMessage would do.
   */
  const executeAction = useCallback(async (toolName: string, args: Record<string, unknown>) => {
    if (state.isLoading) return
    setState(s => ({ ...s, isLoading: true, error: null }))
    try {
      const result = await mcpClient.callTool(toolName, args)
      addMessage({
        role: 'assistant',
        content: result.content[0]?.text ?? 'Done.',
        meta: result._meta,
        isError: result.isError,
      })
      processMeta(result._meta)
      storeSetPending(null)
      setState(s => ({ ...s, isLoading: false }))
    } catch (err) {
      addMessage({
        role: 'assistant',
        content: `Sorry, something went wrong: ${err instanceof Error ? err.message : 'Unknown error'}`,
        isError: true,
      })
      setState(s => ({ ...s, isLoading: false }))
    }
  }, [addMessage, processMeta, storeSetPending])

  return {
    messages,
    isLoading: state.isLoading,
    isModelConnected: state.isModelConnected,
    error: state.error,
    labelCatalog: state.labelCatalog,
    sendMessage,
    executeAction,
    stop,
    checkModelConnected,
  }
}

// ============================================================
// Quality gate — match the server-side `query_too_short_for_tier2`
// floor so degenerate inputs (`?`, `xx`, `aaaa`) never round-trip the
// MCP. Architecturally this is the assistant layer's responsibility:
// reject inputs the search layer can't possibly answer, give the user
// immediate feedback, save the network call. The matching server-side
// floor lives in
// Agion-Intelligence/crates/intelligence-search/src/retrieve_dispatcher.rs
// (`query_too_short_for_tier2` — same 3-alpha-chars + 2-distinct-letters
// thresholds, same Unicode-aware character class).
// ============================================================

/**
 * True when the query has too little signal for the search layer to
 * do meaningful work. The thresholds mirror
 * `query_too_short_for_tier2` on the server:
 *   - fewer than 3 alphabetic characters total, OR
 *   - fewer than 2 distinct alphabetic letters
 *
 * Slash commands (`/help`, `/whoami`) bypass the check — they have a
 * deterministic dispatch path that doesn't go through any embedder.
 *
 * Exported for unit-test coverage (queryQuality.test.ts).
 */
export function isQueryTooDegenerate(raw: string): boolean {
  const trimmed = raw.trim()
  if (trimmed.length === 0) return true
  // Slash commands route deterministically and don't need semantic content.
  if (trimmed.startsWith('/')) return false
  // Short deterministic chat controls must reach executeIntent instead of
  // being treated as semantic-search queries.
  if (/^(hi|hello|hey|help|status|overview|briefing|yes|confirm|ok)$/i.test(trimmed)) return false

  // Unicode-aware: matches Latin, Finnish ä/ö/å, Swedish, etc.
  // The `u` flag + `\p{L}` keeps this in sync with the Rust side, which
  // uses `char::is_alphabetic`.
  const alpha = Array.from(trimmed).filter(c => /\p{L}/u.test(c))
  if (alpha.length < 3) return true

  const distinct = new Set(alpha.map(c => c.toLowerCase()))
  return distinct.size < 2
}

// ============================================================
// Intent execution — search-first pipeline
// ============================================================

// Filler words that carry zero intent signal. Stripped from the start
// and end of user messages before regex routing so that "then how many
// movies?" and "how many movies?" follow the same code path. Reason we
// saw them matter at all: several regex routes in this module anchor
// on ^ (start) or $ (end) — a leading "then" pushes the real verb out
// of ^-anchored matches and the intent gets misclassified as a knowledge
// query.
//
// Multi-word fillers ("by the way", "to be honest") are handled before
// single-word ones so the longer match wins. Punctuation immediately
// after a leading filler ("well, how many…") and before a trailing one
// ("…movies, please") is absorbed into the stripped span.
const LEADING_FILLERS = [
  'by the way',
  'to be honest',
  'as a matter of fact',
  'just checking',
  'just curious',
  'could you',
  'can you',
  'please',
  'ok',
  'okay',
  'well',
  'hmm',
  'hmmm',
  'uh',
  'um',
  'ahh',
  'so',
  'now',
  'then',
  'also',
  'and',
  'but',
  'hey',
  'yo',
  'actually',
  'basically',
  'btw',
  'just',
] as const

const TRAILING_FILLERS = [
  'please',
  'thanks',
  'thank you',
  'then',
  'now',
  'also',
  'actually',
  'btw',
  'okay',
  'ok',
] as const

/** Normalises user input by trimming non-signal filler words off both
 *  ends. Preserves inner-sentence words (classifiers do treat "find X
 *  and Y" differently from "find X, Y"), and is idempotent — running
 *  it twice returns the same string. Never strips the whole message:
 *  if stripping would leave the body empty (e.g. "please" alone), the
 *  original text is returned so the message still reaches the pipeline.
 *
 *  Exported for unit tests. Not exported from the assistant module's
 *  public index — consumers go through the hook. */
export function stripFillers(raw: string): string {
  const original = raw
  let text = raw.trim()
  if (!text) return raw

  // Loop until a pass doesn't change anything; handles chains like
  // "ok so then how many …".
  let changed = true
  while (changed) {
    changed = false

    for (const filler of LEADING_FILLERS) {
      const pattern = new RegExp(`^${escapeRegex(filler)}[,.\\s:;!?-]+`, 'i')
      const next = text.replace(pattern, '')
      if (next !== text) {
        text = next.trimStart()
        changed = true
        break
      }
    }
    if (changed) continue

    for (const filler of TRAILING_FILLERS) {
      const pattern = new RegExp(`[,.\\s:;!?-]+${escapeRegex(filler)}[.!?]*$`, 'i')
      const next = text.replace(pattern, '')
      if (next !== text) {
        text = next.trimEnd()
        changed = true
        break
      }
    }
  }

  // Don't return a stub. If everything was filler, the original message
  // goes through and the LLM can classify it as conversation.
  const cleaned = text.trim()
  return cleaned.length === 0 ? original : cleaned
}

function escapeRegex(s: string): string {
  return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
}

/**
 * Routes a user utterance to the right MCP tool.
 *
	 * Move 1 — Assistant Search Plane v1.0 (2026-04-22): all read intents fan into
	 * one server-side dispatcher (`assistant_search`) instead of the per-entity
 * `list_*` table or the LLM-backed `classify_intent`. Reads include
 * bare nouns, list/show verbs, analytical questions, and capsule-data
 * questions. The frontend stays the deterministic "is this a write,
 * a UX action, or a read?" gate; the dispatcher owns mode selection,
 * scope resolution, and rendering shape.
 *
 * Bypass paths (unchanged) — these never produce useful retrieval:
 *   1. Confirmation flow ("yes" after preview)
 *   2. Slash commands (`/toolname {json}`)
 *   3. Client-side navigation ("take me to missions")
 *   4. Greetings / help / whoami
 *   5. Action verbs (create / invite / connect / configure / …)
 *
	 * Everything else → `assistant_search` with origin=assistant + page_context.
 *
 * Exported so the routing contract has direct unit-test coverage
 * (executeIntent.routing.test.ts).
 */
export async function executeIntent(
  text: string,
  pending: PendingConfirmation | null,
  currentRoute: string,
  navigate: (path: string) => void,
  labelCatalog: LabelCatalog | null,
): Promise<MCPToolResult & { _pending?: PendingConfirmation }> {
  // Strip leading/trailing filler words (then/so/also/please/ok/btw/…)
  // BEFORE any regex routing — otherwise "then how many movies?" gets
  // rejected by the start-anchored action/navigation regexes even though
  // the intent is clear. stripFillers also normalises stray punctuation
  // around the fillers ("well, how many..." / "..., please") so the
  // intent body lands clean.
  text = stripFillers(text)
  const lower = text.toLowerCase().trim()

  // 1. Confirmation flow: "yes" confirms the last preview
  if (/^(yes|confirm|do it|go ahead|proceed|ok|sure|approve it)$/i.test(lower) && pending) {
    return mcpClient.callTool(pending.toolName, { ...pending.args, confirm: true })
  }

  // 2. Slash commands: /toolname {json}
  const slashMatch = lower.match(/^\/(\w+)\s*(.*)/)
  if (slashMatch) {
    const name = slashMatch[1] ?? ''
    const argsStr = slashMatch[2] ?? ''
    try {
      return mcpClient.callTool(name, argsStr ? JSON.parse(argsStr) : {})
    } catch {
      return mcpClient.callTool(name)
    }
  }

  // 3. Navigation intents (client-side, no search needed)
  const navMatch = lower.match(/^(?:take me to|open|go to|navigate to)\s+(.+)/)
  if (navMatch) {
    const dest = navMatch[1] ?? ''
    const route = resolveNavigation(dest)
    if (route) {
      navigate(route)
      return textResponse(`Navigated to ${dest}.`)
    }
  }

  // 4. Greetings / orientation
  if (/^(hi|hello|hey|good morning|good afternoon|what's up)(\s|$)/.test(lower)) {
    return mcpClient.callTool('briefing')
  }
  if (/^(help|what can you|how do i|commands|tools|what do you)/.test(lower)) {
    return mcpClient.callTool('help')
  }
  if (/who am i|my account|my profile|my role/.test(lower)) {
    return mcpClient.callTool('whoami')
  }
  if (/^(status|overview|briefing)/.test(lower)) {
    return mcpClient.callTool('briefing')
  }

  // 5. Direct action routing — action verbs route to tools without context search.
  //    This avoids the BYOH round-trip for clear write intents.
  //    Also catches verb-less patterns like "I need a new rule" or "we should add a capsule".
  if (/^(create|add|new|set up|make|setup|configure|connect|invite|delete|remove|enable|disable|toggle|launch|deploy|block|deny|allow|set|change|update|approve|reject|revoke)\b/.test(lower)
      || /\b(i need|i want|we need|we should|can you|could you|please)\b.*(rule|mission|capsule|label|agent|user|resource|provider|model|escalation|taxonomy)/i.test(lower)) {
    return handleActionIntent(text, labelCatalog)
  }

  // 6. Reads — everything else flows through the assistant search plane.
  //    The dispatcher (intelligence-zone) owns mode selection (semantic /
  //    structured / hybrid / compose / navigate), scope resolution
  //    (capsule slug + page-context auto-fill), persona-aware authz,
  //    and decision audit. Frontend stops second-guessing the LLM and
  //    stops maintaining a per-entity routing table that drifts from the
  //    real catalog.
  //
  //    Plan: plans/engineering/2026-04-22-unified-retrieval-plane.md (Move 1).
  //    Replaces: tryDirectQueryRoute table + classify_intent LLM fallback.
  const auditAnswer = await answerAuditPageQuestion(text, currentRoute)
  if (auditAnswer) return auditAnswer

  const retrieveArgs = buildAssistantRetrieveArgs(text, currentRoute)
  try {
    return await mcpClient.callTool('assistant_search', retrieveArgs)
  } catch {
    return textResponse('I could not process that. Try a direct command like "show missions" or "create a rule".')
  }
}

async function answerAuditPageQuestion(text: string, currentRoute: string): Promise<MCPToolResult | null> {
  const surface = auditSurfaceFromRoute(text, currentRoute)
  if (!surface) return null

  const query = { limit: 5, offset: 0 }
  switch (surface) {
    case 'learnings': {
      const response = await auditService.queryLearningAudit(query)
      const lines = response.items.slice(0, 5).map((item, index) => {
        const source = item.sourceMissionName || item.sourceToolName || item.sourceEventType || 'audit evidence'
        const title = item.title || item.situationSummary || `Learning ${index + 1}`
        const benefit = item.outcomeSummary ? ` Outcome: ${item.outcomeSummary}.` : ''
        const context = item.contextPackHash ? ' Context pack available.' : ''
        return `${index + 1}. ${title} (${item.learningType || 'learning'}, confidence ${formatPercent(item.confidence)}). Source: ${source}.${benefit}${context}`
      })
      return textResponse(lines.length > 0
        ? `The Learnings tab shows promoted learnings that are eligible for agent context injection:\n${lines.join('\n')}`
        : 'The Learnings tab has no promoted learnings in the current audit window.')
    }
    case 'assistant': {
      const response = await auditService.queryAssistantSessions(query)
      const lines = response.items.slice(0, 5).map((item, index) => {
        const user = item.userName || item.memberId || 'Unknown user'
        const learning = item.learningEligible ? 'learning-eligible' : 'audit-only'
        return `${index + 1}. ${user}: ${item.toolCallCount} tool calls, ${item.successCount} succeeded, ${item.errorCount} failed, ${learning}.`
      })
      return textResponse(lines.length > 0
        ? `The Agion Assistant audit tab shows assistant sessions and tool activity:\n${lines.join('\n')}`
        : 'The Agion Assistant audit tab has no assistant sessions in the current audit window.')
    }
    case 'designer': {
      const response = await auditService.queryDesignerSessions(query)
      const lines = response.items.slice(0, 5).map((item, index) => {
        const mission = item.missionName || 'Untitled mission'
        const event = humanizeAuditToken(item.eventType || 'designer event')
        return `${index + 1}. ${mission}: ${event}, ${item.successCount} succeeded, ${item.errorCount} failed.`
      })
      return textResponse(lines.length > 0
        ? `The Mission Designer audit tab shows mission design activity:\n${lines.join('\n')}`
        : 'The Mission Designer audit tab has no design activity in the current audit window.')
    }
    case 'resources': {
      const response = await auditService.queryResourceSessions(query)
      const lines = response.items.slice(0, 5).map((item, index) => {
        const name = item.resourceName || item.resourceType || item.category || 'Resource'
        const action = humanizeAuditToken(item.action || item.eventType || 'changed')
        const status = item.errorCount > 0 ? 'failed' : 'succeeded'
        return `${index + 1}. ${name}: ${action} ${status}.`
      })
      return textResponse(lines.length > 0
        ? `The Resources audit tab shows provider, model, tool, and resource administration:\n${lines.join('\n')}`
        : 'The Resources audit tab has no resource administration activity in the current audit window.')
    }
    case 'intelligence': {
      const hostsResponse = await agentHostService.listHosts()
      const hosts = (hostsResponse.hosts || [])
        .filter(host => host.status === 'online' || host.status === 'degraded')
        .slice(0, 3)
      const settled = await Promise.allSettled(hosts.map(async host => {
        const audit = await intelligenceService.listAuditEvents(host.id, { limit: 20 }, { timeout: 6000, retry: 0 })
        return { hostName: host.name || host.id, events: audit.events || [] }
      }))
      const summaries = settled.flatMap(result => {
        if (result.status !== 'fulfilled') return []
        const events = result.value.events
        const latest = events[0]
        return [{
          hostName: result.value.hostName,
          eventCount: events.length,
          latestType: latest?.eventType || 'no recent event',
          latestCapsule: latest?.capsuleId || 'unassigned capsule',
        }]
      })
      const lines = summaries.slice(0, 5).map((item, index) =>
        `${index + 1}. ${item.hostName}: ${item.eventCount} events, latest ${humanizeAuditToken(item.latestType)} for ${item.latestCapsule}.`
      )
      return textResponse(lines.length > 0
        ? `The Intelligence audit tab shows host-local capsule ingestion and runtime activity:\n${lines.join('\n')}`
        : 'The Intelligence audit tab has no reachable host audit activity in the current audit window.')
    }
    case 'missions':
    default: {
      const response = await auditService.queryMissionSummaries({ ...query, sortBy: 'lastRun', sortOrder: 'desc' })
      const lines = response.items.slice(0, 5).map((item, index) => {
        const name = item.missionName || 'Untitled mission'
        return `${index + 1}. ${name}: ${item.executionCount} executions, ${item.totalEvents} events, ${item.successCount} succeeded, ${item.failureCount} failed, ${item.learningEligibleCount} learning-eligible.`
      })
      return textResponse(lines.length > 0
        ? `The Mission Executions audit tab shows mission-level execution history:\n${lines.join('\n')}`
        : 'The Mission Executions audit tab has no mission activity in the current audit window.')
    }
  }
}

type AuditSurface = 'missions' | 'assistant' | 'designer' | 'resources' | 'intelligence' | 'learnings'

function auditSurfaceFromRoute(text: string, currentRoute: string): AuditSurface | null {
  const pathAndQuery = currentRoute || ''
  const path = pathAndQuery.split('?')[0] || '/'
  if (!path.startsWith('/audit')) return null
  if (path.startsWith('/audit/intelligence')) return 'intelligence'

  const lower = text.toLowerCase()
  if (/\b(learning|learnings|precedent|context pack|remember|agent context)\b/.test(lower)) return 'learnings'
  if (/\b(assistant|chat|own actions|tool call|tool calls)\b/.test(lower)) return 'assistant'
  if (/\b(designer|mission design|draft|canvas)\b/.test(lower)) return 'designer'
  if (/\b(resource|resources|provider|providers|model|models|tool|tools)\b/.test(lower)) return 'resources'
  if (/\b(intelligence|capsule|ingestion|sync|classif|rag)\b/.test(lower)) return 'intelligence'

  try {
    const url = new URL(pathAndQuery, 'https://admin.local')
    const tab = url.searchParams.get('tab')
    if (tab === 'assistant' || tab === 'designer' || tab === 'resources' || tab === 'intelligence' || tab === 'learnings') {
      return tab
    }
  } catch {
    // Keep the audit page helpful even when tests pass a path-only route.
  }

  return 'missions'
}

function humanizeAuditToken(value: string): string {
  return value.replace(/[_:.]+/g, ' ').replace(/\s+/g, ' ').trim()
}

function formatPercent(value: number): string {
  if (!Number.isFinite(value)) return 'unknown'
  const normalized = value > 1 ? value : value * 100
  return `${Math.round(normalized)}%`
}

export function buildAssistantRetrieveArgs(text: string, currentRoute: string): Record<string, unknown> {
  const lower = text.toLowerCase()
  const context: Record<string, unknown> = {
    origin: 'assistant',
    page_context: currentRoute,
  }
  const scope: Record<string, unknown> = {}

  const capsuleId = extractCapsuleIdFromRoute(currentRoute)
  if (capsuleId) {
    scope.capsule_ids = [capsuleId]
  }

  const isCrossResourceCapsuleSearch =
    /\b(resource|resources|connection|connections|database|api|apis)\b/.test(lower) &&
    /\b(capsule|capsules|knowledge)\b/.test(lower)

  const routeHostId = extractInfrastructureHostIdFromRoute(currentRoute)
  if (routeHostId) {
    context.host_id = routeHostId
  } else if (isCrossResourceCapsuleSearch) {
    const selectedHostId = readSelectedIntelligenceHostId()
    if (selectedHostId) {
      context.host_id = selectedHostId
    }
  }

  const args: Record<string, unknown> = { query: text, context }
  if (isCrossResourceCapsuleSearch) {
    args.sources = ['resource_catalog', 'capsules']
    scope.entity_kinds = ['resource', 'capsule']
    scope.include_platform_catalog = false
    args.budget = { max_results: 20 }
  }
  if (isGovernedResourceRoute(currentRoute)) {
    args.sources = ['resource_catalog']
    scope.entity_kinds = ['resource']
    args.budget = { max_results: 20 }
  }
  if (Object.keys(scope).length > 0) {
    args.scope = scope
  }
  return args
}

function extractCapsuleIdFromRoute(route: string): string | null {
  const path = route.split('?')[0] ?? route
  const match = path.match(/^\/intelligence\/capsules\/([^/]+)/)
  if (!match?.[1] || match[1] === 'new') return null
  return decodeURIComponent(match[1])
}

function extractInfrastructureHostIdFromRoute(route: string): string | null {
  const path = route.split('?')[0] ?? route
  const match = path.match(/^\/infrastructure\/hosts\/([^/]+)/)
  if (!match?.[1]) return null
  return decodeURIComponent(match[1])
}

function readSelectedIntelligenceHostId(): string | null {
  if (typeof window === 'undefined') return null
  try {
    const raw = window.localStorage.getItem('agion.intelligence.target')
    if (!raw) return null
    const parsed = JSON.parse(raw) as { type?: string; hostId?: string; resolvedHostId?: string }
    if (parsed.type === 'host' && parsed.hostId) return parsed.hostId
    if (parsed.type === 'group' && parsed.resolvedHostId) return parsed.resolvedHostId
  } catch {
    return null
  }
  return null
}

function isGovernedResourceRoute(route: string): boolean {
  const path = route.split('?')[0] ?? route
  return path === '/governance/resources' ||
    path === '/governance/resources/connections' ||
    path.startsWith('/governance/resources/connection/')
}

/** Handle action intents: route to the appropriate write tool */
async function handleActionIntent(
  text: string,
  labelCatalog: LabelCatalog | null,
): Promise<MCPToolResult & { _pending?: PendingConfirmation }> {
  const lower = text.toLowerCase()
  const emailMatch = text.match(/(\S+@\S+\.\S+)/i)

  // Helper: navigate to a UI page with a message
  const navTo = (msg: string, route: string): MCPToolResult =>
    ({ content: [{ type: 'text', text: msg }], _meta: { navigateTo: route } }) as MCPToolResult

  // --- Rule actions (can be done in chat — no IDs needed for create) ---
  if (/rule|policy/.test(lower) && !/escalation/.test(lower)) {
    if (/create|add|new|set up|make/.test(lower)) return createRuleFromText(text, labelCatalog)
    if (/delete|remove|disable|enable|toggle|turn/.test(lower)) {
      return navTo('Opening the governance rules page where you can manage individual rules.', '/governance')
    }
  }

  // --- Mission actions ---
  if (/mission/.test(lower)) {
    if (/create|new|build|add/.test(lower)) {
      return navTo('Opening the mission designer where you can create a new mission.', '/missions/new')
    }
    if (/launch|start|run|delete|archive|manage/.test(lower)) {
      return navTo('Opening the missions page.', '/missions')
    }
  }

  // --- Provider / model actions ---
  const providerMatch = lower.match(/(openai|anthropic|azure|vertex|google|cerebras|huggingface|elevenlabs|ollama)/)
  if (providerMatch) {
    const providerType = providerMatch[1] === 'google' ? 'vertex' : providerMatch[1]
    // Detect "add MODEL from PROVIDER" — model name present means model management, not provider setup
    const hasModelName = /gpt|claude|gemini|llama|mistral|qwen|phi|codex|nano|mini|turbo|sonnet|opus|haiku/i.test(lower)
    if (hasModelName) {
      // Navigate to provider detail page where user can manage models
      return navTo(
        `Opening the ${providerType} provider page where you can manage available models.`,
        `/governance/resources/providers/${providerType}-connection`,
      )
    }
    if (/connect|add|setup|configure/.test(lower)) {
      return mcpClient.callTool('setup_provider', { type: providerType })
    }
  }
  if (/provider/.test(lower) && /connect|add|setup|configure/.test(lower)) {
    return navTo('Opening the providers page where you can connect a new AI provider.', '/governance/resources/providers')
  }

  // --- Resource / database / API actions ---
  if (/connect|add/.test(lower) && /database|db|postgres|mysql|api|slack|resource|s3|storage/.test(lower)) {
    const typeMatch = lower.match(/(postgres(?:ql)?|mysql|mongodb|slack|zendesk|shopify|aws.?s3|azure.?blob|rest.?api|git)/)
    const type = typeMatch?.[1]?.replace(/\s+/g, '-').replace(/^postgres$/, 'postgresql')
    return mcpClient.callTool('connect_resource', type ? { type } : {})
  }

  // --- Capsule actions ---
  if (/capsule|knowledge.?base|data.?source/.test(lower)) {
    if (/create|add|new/.test(lower)) return navTo('Opening the capsule creation page.', '/intelligence/capsules/new')
    if (/sync|refresh|re-?index/.test(lower)) return navTo('Opening the capsules page where you can trigger a sync.', '/intelligence/capsules')
    if (/manage|edit|update|delete/.test(lower)) return navTo('Opening the capsules page.', '/intelligence/capsules')
  }

  // --- Label actions (simple enough for chat) ---
  if (/label/.test(lower) && /create|add|new/.test(lower)) {
    const nameMatch = text.match(/label\s+(?:called|named)?\s*"?([^"]+)"?/i)
    if (nameMatch?.[1]) return mcpClient.callTool('manage_labels', { action: 'create', name: nameMatch[1].trim() })
    return navTo('Opening governance settings where you can manage labels.', '/governance')
  }

  // --- Agent actions ---
  if (/agent/.test(lower) && !/user.?agent/.test(lower)) {
    return navTo('Opening the agents page.', '/agents')
  }

  // --- Escalation actions ---
  if (/escalation/.test(lower)) {
    return navTo('Opening the escalations page where you can review and resolve pending items.', '/governance/escalations')
  }

  // --- Trust actions ---
  if (/trust/.test(lower) && /configure|set|change|update|manage/.test(lower)) {
    return navTo('Opening the trust management page.', '/governance/trust')
  }

  // --- Organization actions ---
  if (/organi[sz]ation|org\b/.test(lower) && /rename|change|update|setting|manage/.test(lower)) {
    return navTo('Opening organization settings.', '/organization')
  }

  // --- User actions (email-based invite works in chat) ---
  if (emailMatch && /invite/.test(lower)) {
    const roleMatch = text.match(/as\s+(admin|manager|user|auditor)/i)
    const args = { email: emailMatch[1], role: roleMatch?.[1] ?? 'user' }
    const result = await mcpClient.callTool('invite_user', args) as MCPToolResult & { _pending?: PendingConfirmation }
    result._pending = { toolName: 'invite_user', args }
    return result
  }
  if (/invite|add\s+(?:a\s+)?(?:user|member)/.test(lower)) {
    return textResponse('To invite someone, tell me their email. For example:\n"Invite john@example.com as admin"')
  }

  // --- Model default (simple enough for chat) ---
  if (/set.*(?:default\s+)?model|change.*model/.test(lower)) {
    return mcpClient.callTool('set_model_default')
  }

  // --- Domain actions (simple enough for chat) ---
  const domainMatch = text.match(/(?:add|allow)\s+(?:domain|email domain)\s+(\S+)/i)
  if (domainMatch?.[1]) {
    const args = { action: 'add_domain', domain: domainMatch[1] }
    const result = await mcpClient.callTool('org_settings', args) as MCPToolResult & { _pending?: PendingConfirmation }
    result._pending = { toolName: 'org_settings', args }
    return result
  }

  // --- Host / infrastructure actions ---
  if (/host|infrastructure|server|vm/.test(lower)) {
    return navTo('Opening the infrastructure page.', '/infrastructure/hosts')
  }

  // --- Audit / compliance ---
  if (/audit|compliance|decision/.test(lower)) {
    return navTo('Opening the audit log.', '/audit')
  }

  // --- Fallback: an action verb matched but no specific template did
  //     (e.g. "approve all pending escalations"). Route through the
  //     assistant search plane — `mode=auto` will resolve to navigate
  //     for entity references and to structured for filtered listings.
  return mcpClient.callTool('assistant_search', {
    query: text,
    context: { origin: 'assistant', surface: 'action_fallback' },
  })
}

/**
 * Extract structured rule parameters from a natural-language sentence
 * and call create_rule.
 *
 * Label-aware AND-aware parser:
 *
 *   1. Strips trust-tier filter ("lower than elevated", "≤ standard").
 *
 *   2. Splits the remaining text on AND boundaries (the words "and",
 *      "AND", "&", commas, plus "when ... and") into clauses.
 *
 *   3. Matches each clause against the org's GovernanceLabel
 *      catalog using bag-of-tokens + synonyms grounded in the
 *      actual labels of the dev cluster (data classification,
 *      AI risk level, geographic, lawful basis, processing type,
 *      purpose). Up to 3 clauses are kept (1 primary +
 *      2 and_conditions, matching the backend cap).
 *
 *   4. Falls back to the canonical hardcoded mapping when no label
 *      catalog is available or no label scored above zero.
 */
async function createRuleFromText(
  text: string,
  labelCatalog: LabelCatalog | null,
): Promise<MCPToolResult & { _pending?: PendingConfirmation }> {
  let working = text.replace(/^(?:create|add|new|set up|make)\s+(?:a\s+|an\s+)?/i, '').trim()

  // --- 1. Action ---
  let action = 'deny'
  if (/obfuscat|mask|redact|sanitiz|anonymi[sz]/i.test(working)) action = 'obfuscate'
  else if (/block|deny|prevent|forbid/i.test(working)) action = 'deny'
  else if (/escalat|approv|review|human[- ]in[- ]the[- ]loop/i.test(working)) action = 'escalate'
  else if (/allow|permit/i.test(working)) action = 'allow'
  else if (/audit|log|track/i.test(working)) action = 'audit'

  // --- 2. Trust-tier filter (extracted FIRST and removed) ---
  let maxTrustTier: string | undefined
  const lowerThan = working.match(
    /(?:trust(?:\s+tier)?\s+(?:is\s+)?)?(?:lower|less|below)(?:\s+than)?\s+(restricted|limited|standard|elevated|trusted|untrusted|basic|normal)/i,
  )
  if (lowerThan) {
    const tier = (lowerThan[1] ?? '').toLowerCase()
    const oneBelow: Record<string, string> = {
      elevated: 'standard', trusted: 'standard',
      standard: 'limited', normal: 'limited',
      limited: 'restricted', basic: 'restricted',
      restricted: 'restricted', untrusted: 'restricted',
    }
    maxTrustTier = oneBelow[tier] ?? tier
    working = working.replace(lowerThan[0], '')
  } else {
    const atOrBelow = working.match(
      /(?:trust(?:\s+tier)?\s+)?(?:<=|≤|at\s+or\s+below|no\s+higher\s+than)\s*(restricted|limited|standard|elevated|trusted|untrusted|basic|normal)/i,
    )
    if (atOrBelow) {
      maxTrustTier = (atOrBelow[1] ?? '').toLowerCase()
      working = working.replace(atOrBelow[0], '')
    }
  }

  // --- 3. Split into AND clauses ---
  // The split happens on " and ", commas, and "&". The "for ..."
  // suffix is stripped first because it's almost always tier-related.
  working = working.replace(/\s+for\s+(?:any\s+)?(?:agents?|users?)\b.*$/i, '')
  const clauses = working
    .split(/\s+and\s+|\s*,\s*|\s*&\s*/i)
    .map(c => c.trim())
    .filter(c => c.length > 0)

  // --- 4. Match each clause against the label catalog ---
  type MatchedClause = {
    field: string
    operator: string
    value: string
    displayName?: string
  }
  const matched: MatchedClause[] = []
  for (const clause of clauses) {
    if (matched.length >= 3) break // backend cap = 1 primary + 2 ands
    const m = matchClauseToCondition(clause, labelCatalog)
    if (m) {
      // De-duplicate: skip if we already have this exact (field, value)
      if (!matched.some(p => p.field === m.field && p.value === m.value)) {
        matched.push(m)
      }
    }
  }

  // If no clause matched anything, run the legacy fallback once
  // against the whole working text so we always produce SOMETHING.
  if (matched.length === 0) {
    const fb = fallbackConditionFromText(working)
    matched.push({ field: fb.field, operator: 'eq', value: fb.value })
  }

  const primary = matched[0]!
  const ands = matched.slice(1)

  // --- 5. Rule name ---
  const nameVerb = action === 'deny' ? 'Block'
    : action === 'obfuscate' ? 'Obfuscate'
      : action === 'escalate' ? 'Escalate'
        : action === 'audit' ? 'Audit'
          : 'Allow'
  const nameSubject = primary.displayName
    || (primary.field === 'content.has_pii' ? 'PII'
      : primary.field === 'content.has_financial_data' ? 'financial data'
        : primary.field === 'content.has_credentials' ? 'credentials'
          : primary.value)
  const tierSuffix = maxTrustTier ? ` for non-${maxTrustTier} agents` : ''
  const name = `${nameVerb} ${nameSubject}${tierSuffix}`.slice(0, 80)

  const args: Record<string, unknown> = {
    name,
    condition_field: primary.field,
    condition_operator: primary.operator,
    condition_value: primary.value,
    action,
  }
  if (ands.length > 0) {
    args.and_conditions = ands.map(a => ({
      field: a.field,
      operator: a.operator,
      value: a.value,
    }))
  }
  if (maxTrustTier) args.max_trust_tier = maxTrustTier

  const result = await mcpClient.callTool('create_rule', args) as MCPToolResult & { _pending?: PendingConfirmation }
  result._pending = { toolName: 'create_rule', args }
  return result
}

/**
 * matchClauseToCondition attempts to map a single AND-clause to a
 * condition. Tries (in order):
 *   1. Label match against the org's catalog (best score wins).
 *   2. Hardcoded canonical-field mapping for things that aren't
 *      governance labels (content.has_pii, context.environment, etc.).
 *
 * Returns null when neither path produces anything — the caller
 * skips the clause and continues with whatever did match.
 */
function matchClauseToCondition(
  clause: string,
  labelCatalog: LabelCatalog | null,
): { field: string; operator: string; value: string; displayName?: string } | null {
  if (labelCatalog && labelCatalog.labels.length > 0) {
    const m = matchLabelByPhrase(clause, labelCatalog)
    if (m) {
      return {
        field: `resource.labels.${categoryFieldSegment(m.category)}`,
        operator: 'eq',
        value: m.label.key,
        displayName: m.label.displayName || m.label.key,
      }
    }
  }
  // Canonical-field fallbacks for things that aren't labels.
  if (/credential|secret|api[- ]?key|token|password/i.test(clause)) {
    return { field: 'content.has_credentials', operator: 'eq', value: 'true' }
  }
  if (/financial|payment|card|iban|bank/i.test(clause)) {
    return { field: 'content.has_financial_data', operator: 'eq', value: 'true' }
  }
  if (/production|prod\b/i.test(clause)) {
    return { field: 'context.environment', operator: 'eq', value: 'production' }
  }
  if (/staging\b/i.test(clause)) {
    return { field: 'context.environment', operator: 'eq', value: 'staging' }
  }
  if (/development|dev\s+env/i.test(clause)) {
    return { field: 'context.environment', operator: 'eq', value: 'development' }
  }
  return null
}

/**
 * matchLabelByPhrase scores every governance label in the org's
 * catalog against a phrase using token overlap + a synonyms map
 * grounded in the labels actually shipped on the dev cluster.
 *
 * Synonyms cover the gap between casual user language ("PII",
 * "credit card", "GDPR data") and the canonical label keys
 * ("personal_data", "special_category_data", "consent").
 */
function matchLabelByPhrase(
  phrase: string,
  catalog: LabelCatalog,
): { label: GovernanceLabel; category: LabelCategory } | null {
  const phraseTokens = tokenize(phrase)
  if (phraseTokens.size === 0) return null

  // Synonyms expanded from the dev cluster's actual label catalog
  // (data_classification, ai_risk_level, geographic, lawful_basis,
  // processing_type, purpose). Keys are user-side words; values
  // are tokens that should boost label matches whose displayName,
  // key, or description contains them.
  const SYNONYMS: Record<string, string[]> = {
    // === Data classification ===
    pii: ['personal', 'private', 'sensitive', 'individual', 'identifiable'],
    personal: ['pii', 'private', 'individual', 'identifiable'],
    private: ['pii', 'personal', 'confidential'],
    sensitive: ['confidential', 'restricted', 'pii', 'special'],
    confidential: ['internal', 'restricted', 'private'],
    secret: ['confidential', 'trade', 'proprietary'],
    proprietary: ['trade', 'secret', 'confidential'],
    public: ['open', 'unclassified'],
    internal: ['confidential', 'private'],
    special: ['biometric', 'health', 'religious', 'genetic', 'racial', 'ethnic'],
    biometric: ['special', 'identification', 'fingerprint', 'face'],
    health: ['special', 'medical', 'patient'],

    // === AI risk level (EU AI Act) ===
    high: ['risk', 'dangerous'],
    minimal: ['low', 'safe'],
    prohibited: ['banned', 'forbidden', 'illegal', 'unacceptable'],
    unacceptable: ['prohibited', 'banned'],
    banned: ['prohibited', 'unacceptable'],

    // === Geographic ===
    eu: ['european', 'eea', 'gdpr', 'europe'],
    european: ['eu', 'eea', 'europe'],
    gdpr: ['eu', 'european', 'personal'],
    us: ['american', 'usa', 'states'],
    uk: ['british', 'kingdom'],
    nordic: ['nordics', 'finland', 'sweden', 'norway', 'denmark'],

    // === Lawful basis ===
    consent: ['agreement', 'opt', 'permission'],
    contract: ['agreement', 'commercial'],
    legitimate: ['interest', 'interests'],
    obligation: ['legal', 'mandatory'],
    vital: ['life', 'safety', 'emergency'],
    public_task: ['authority', 'official'],

    // === Processing type ===
    profiling: ['automated', 'scoring', 'predictive'],
    automated: ['profiling', 'decision', 'algorithmic'],
    scoring: ['profiling', 'social', 'rating'],
    employment: ['hiring', 'work', 'job', 'recruitment'],
    education: ['student', 'school', 'assessment'],
    law: ['enforcement', 'police', 'criminal'],
    enforcement: ['law', 'police', 'criminal'],
    migration: ['border', 'immigration'],
    justice: ['court', 'judicial'],
    critical: ['infrastructure', 'utility'],
    essential: ['services', 'utility'],

    // === Purpose ===
    marketing: ['advertising', 'promotion', 'campaign'],
    analytics: ['analysis', 'insights', 'metrics', 'data'],
    security: ['fraud', 'prevention', 'protection'],
    fraud: ['security', 'prevention', 'detection'],
    compliance: ['legal', 'regulation'],
    research: ['scientific', 'study', 'experiment'],
  }

  const expanded = new Set<string>(phraseTokens)
  for (const tok of Array.from(phraseTokens)) {
    const ext = SYNONYMS[tok]
    if (ext) ext.forEach(t => expanded.add(t))
  }

  let best: { label: GovernanceLabel; category: LabelCategory; score: number } | null = null

  for (const label of catalog.labels) {
    const labelTokens = new Set([
      ...tokenize(label.displayName || ''),
      ...tokenize(label.key || ''),
      ...tokenize(label.description || ''),
    ])
    let score = 0
    for (const tok of expanded) {
      if (labelTokens.has(tok)) score += 1
    }
    // Strong boosts for direct matches
    if (label.key && phraseTokens.has(label.key.toLowerCase())) score += 5
    if (label.displayName && phrase.toLowerCase().includes(label.displayName.toLowerCase())) score += 8

    if (score > 0 && (!best || score > best.score)) {
      const category = catalog.categories.find(c => c.id === label.categoryId)
      if (category) {
        best = { label, category, score }
      }
    }
  }
  return best
}

function tokenize(text: string): Set<string> {
  const stop = new Set([
    'a','an','and','or','of','the','to','in','for','on','at','by','from','with',
    'is','are','was','were','be','been','rule','when','that','this','agents','agent',
    'create','add','new','setup','set','make','obfuscate','block','deny','allow','escalate',
    'audit','data','trust','tier','lower','than','below','elevated','restricted','limited','standard',
    'all','any','some','such','it','as','if','then','also','very','more','most',
  ])
  return new Set(
    text
      .toLowerCase()
      .replace(/[^a-z0-9\s]/g, ' ')
      .split(/\s+/)
      .filter(t => t.length >= 3 && !stop.has(t)),
  )
}

function categoryFieldSegment(category: LabelCategory): string {
  return (category.name || '').toLowerCase().replace(/\s+/g, '_')
}

function fallbackConditionFromText(text: string): { field: string; value: string } {
  if (/credential|secret|api[- ]?key|token|password/i.test(text)) {
    return { field: 'content.has_credentials', value: 'true' }
  }
  if (/financial|payment|card|iban|bank/i.test(text)) {
    return { field: 'content.has_financial_data', value: 'true' }
  }
  if (/(?:data.*(?:is|are|classified|marked).*(?:pii|personal|sensitive))|(?:pii|personal\s+data|sensitive\s+data)/i.test(text)) {
    return { field: 'mission.data_classification', value: 'pii' }
  }
  if (/confidential/i.test(text)) {
    return { field: 'mission.data_classification', value: 'confidential' }
  }
  if (/high[- ]risk|unacceptable\s+risk/i.test(text)) {
    return { field: 'mission.ai_risk_level', value: 'high' }
  }
  if (/(?:data|source|resource|storage|stored|processing|residen(?:cy|t)|location).*(?:\bEU\b|european|gdpr)/i.test(text)) {
    return { field: 'data.geographic_region', value: 'eu' }
  }
  if (/(?:data|source|resource|storage|stored|processing|residen(?:cy|t)|location).*(?:\bUS\b|usa|united states)/i.test(text)) {
    return { field: 'data.geographic_region', value: 'us' }
  }
  if (/(?:data|source|resource|storage|stored|processing|residen(?:cy|t)|location).*(?:apac|asia[- ]?pacific)/i.test(text)) {
    return { field: 'data.geographic_region', value: 'asia_pacific' }
  }
  if (/\bEU\b|european|gdpr/i.test(text)) {
    return { field: 'mission.geographic_region', value: 'eu' }
  }
  if (/production|prod\b/i.test(text)) {
    return { field: 'context.environment', value: 'production' }
  }
  return { field: 'content.has_pii', value: 'true' }
}


// ============================================================
// Helpers
// ============================================================

/** Navigation route resolver */
function resolveNavigation(dest: string): string | null {
  const routes: Record<string, string> = {
    'missions': '/missions', 'mission studio': '/missions',
    'mission designer': '/missions/designer',
    'agents': '/agents',
    'governance': '/governance', 'policy center': '/governance', 'policies': '/governance', 'rules': '/governance',
    'trust': '/governance/trust',
    'resources': '/governance/resources', 'providers': '/governance/resources/providers',
    'escalations': '/governance/escalations',
    'hosts': '/infrastructure/hosts', 'infrastructure': '/infrastructure/hosts',
    'capsules': '/intelligence/capsules', 'intelligence': '/intelligence/capsules',
    'taxonomy': '/intelligence/vocabulary?tab=taxonomy',
    'users': '/users', 'team': '/users', 'members': '/users',
    'organization': '/organization', 'org settings': '/organization',
    'settings': '/settings',
    'audit': '/audit', 'audit log': '/audit',
    'analytics': '/analytics',
    'billing': '/billing',
  }
  const lower = dest.toLowerCase().trim()
  return routes[lower] || null
}

/** Create a simple text-only MCPToolResult */
function textResponse(text: string): MCPToolResult {
  return { content: [{ type: 'text', text }] }
}
